import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const UNITS = ['kg', 'g', 'piece', 'dozen'];
const inputCls = 'w-full px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-lg text-sm focus:ring-1 focus:ring-primary outline-none transition-all';

export default function ShopPanel({ onNavigate }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);

  const authHeader = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchProducts = useCallback(() => {
    setLoading(true);
    api.get('/api/butcher/my-listings', authHeader())
      .then(r => setProducts(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/api/butcher/shop/${id}`, authHeader());
    fetchProducts();
  };

  const toggleStatus = async (p) => {
    await api.put(`/api/butcher/shop/${p.id}`, {
      ...p, status: p.status === 'active' ? 'inactive' : 'active'
    }, authHeader());
    fetchProducts();
  };

  const handleEditSave = async () => {
    if (!editing.name || !editing.price_inr) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name',        editing.name);
      fd.append('description', editing.description || '');
      fd.append('price_inr',   editing.price_inr);
      fd.append('stock_qty',   editing.stock_qty);
      fd.append('unit',        editing.unit);
      fd.append('status',      editing.status);
      if (editing.newImageFile) fd.append('product_image', editing.newImageFile);

      await api.put(`/api/butcher/shop/${editing.id}`, fd, authHeader());
      setEditing(null);
      fetchProducts();
    } catch (err) {
      console.error(err);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-bold text-lg">My Shop</h3>
          <button onClick={() => onNavigate('add_product')}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-bold hover:opacity-90 transition-all">
            <span className="material-symbols-outlined text-[18px]">add</span>
            Add Product
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-secondary text-sm">Loading...</div>
        ) : products.length === 0 ? (
          <div className="py-10 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant">storefront</span>
            <p className="mt-2 text-sm font-medium text-secondary">No products yet. Add your first one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map(p => (
              <div key={p.id} className="border border-outline-variant/20 rounded-2xl overflow-hidden hover:shadow-md transition-all">
                {p.image_url
                  ? <img src={p.image_url} alt={p.name} className="w-full h-36 object-cover" />
                  : <div className="w-full h-36 bg-surface-container flex items-center justify-center">
                      <span className="material-symbols-outlined text-4xl text-outline-variant">storefront</span>
                    </div>
                }
                <div className="p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-sm">{p.name}</p>
                      <p className="text-[11px] text-secondary">{p.category} · {p.weight_kg ? `${p.weight_kg}${p.unit}` : p.unit}</p>
                    </div>
                    <p className="font-black text-primary text-sm">₹{Number(p.price_inr).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-secondary">
                      Stock: {p.stock_qty}/{p.initial_stock} · Sold: {(p.initial_stock || 0) - (p.stock_qty || 0)}
                    </span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => toggleStatus(p)}
                      className="flex-1 py-1.5 rounded-lg border border-outline-variant/30 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-all">
                      {p.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => setEditing({ ...p })}
                      className="py-1.5 px-3 rounded-lg bg-blue-50 text-blue-600 text-xs font-bold hover:bg-blue-100 transition-all">
                      <span className="material-symbols-outlined text-sm">edit</span>
                    </button>
                    <button onClick={() => handleDelete(p.id)}
                      className="py-1.5 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 transition-all">
                      <span className="material-symbols-outlined text-sm">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Edit Modal ── */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
             onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 overflow-y-auto max-h-[90vh]"
               onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <h3 className="font-black text-lg">Edit Product</h3>
              <button onClick={() => setEditing(null)}
                className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center">
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Name *</label>
                <input value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                  className={inputCls} placeholder="Product name" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Price (₹) *</label>
                  <input type="number" value={editing.price_inr}
                    onChange={e => setEditing(p => ({ ...p, price_inr: e.target.value }))}
                    className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Stock Qty</label>
                  <input type="number" min="0" value={editing.stock_qty}
                    onChange={e => setEditing(p => ({ ...p, stock_qty: e.target.value }))}
                    className={inputCls} />
                </div>
              </div>

              {Number(editing.price_inr) > 0 && Number(editing.stock_qty) >= 1 && (
                <div className="flex items-center justify-between px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <span className="text-[11px] text-emerald-700 font-semibold">
                    ₹{Number(editing.price_inr).toLocaleString('en-IN')} × {editing.stock_qty} units
                  </span>
                  <span className="text-[13px] text-emerald-800 font-black">
                    = ₹{(Number(editing.price_inr) * Number(editing.stock_qty)).toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Unit</label>
                <select value={editing.unit} onChange={e => setEditing(p => ({ ...p, unit: e.target.value }))}
                  className={inputCls}>
                  {UNITS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Description</label>
                <textarea rows={3} value={editing.description || ''}
                  onChange={e => setEditing(p => ({ ...p, description: e.target.value }))}
                  className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
                  Update Image
                </label>
                {editing.image_url && !editing.newImageFile && (
                  <img src={editing.image_url} alt="current"
                    className="w-20 h-20 rounded-xl object-cover mb-2" />
                )}
                {editing.newImageFile && (
                  <img src={URL.createObjectURL(editing.newImageFile)} alt="new"
                    className="w-20 h-20 rounded-xl object-cover mb-2" />
                )}
                <input type="file" accept="image/*"
                  onChange={e => setEditing(p => ({ ...p, newImageFile: e.target.files[0] }))}
                  className="text-xs text-secondary file:mr-2 file:px-3 file:py-1 file:rounded-full
                            file:border-0 file:bg-primary/10 file:text-primary file:font-bold
                            file:text-xs cursor-pointer" />
              </div>
            </div>

            <button onClick={handleEditSave} disabled={saving}
              className="w-full py-3 rounded-xl bg-primary text-white font-bold text-sm
                         hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
              {saving
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
                : <><span className="material-symbols-outlined text-[18px]">save</span> Save Changes</>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
}